import { flow, Instance, types, getEnv } from "mobx-state-tree";
import { endpoints } from "../api/ApiEndpoints";
import { Column, ColumnType, DisplayValues } from "../../components/datatable/interfaces";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { translationService } from "../translation/TranslationService";
import { formatEuro } from "./formatEuro";

export interface PriceTier {
    id: number,
    minimum: number,
    maximum: number,
    discount: number,
    ratio: number
}

export const emptyPriceTier: PriceTier = {
    id: 0,
    minimum: 0,
    maximum: 0,
    discount: 0,
    ratio: 0
}

// Tiers bound the balance bought, in euro-cents; ratio is the EUR paid per EUR of balance
// (0.95 = 5% bonus). The table shows the balance bounds in € and the discount; ratio is left out.
const columns : Column[] = [
    {
        key: "minimum",
        name: "shop.minimum",
        type: ColumnType.String
    },
    {
        key: "maximum",
        name: "shop.maximum",
        type: ColumnType.String
    },
    {
        key: "discount",
        name: "shop.discount",
        type: ColumnType.String,
        containsLink: true,
    }
]

const PricesModel = types.model({
    prices: types.optional(types.frozen<PriceTier[]>(), [emptyPriceTier, emptyPriceTier]),
})

export const PricesStorageModel = types
    .compose(PricesModel, DataGridStorageModel)
    .named("PricesStorageModel")
    .actions((self) => {
        const tierFor = (balanceCents: number): PriceTier =>
            self.prices.find(p => balanceCents >= p.minimum && balanceCents <= p.maximum)
                ?? self.prices[self.prices.length - 1];

        return {
            mapResponse(response: PriceTier[]) {
                self.prices = response;
                self.gridColumns = columns;
                self.data = {
                    totalCount: response.length,
                    items: response.map(self.mapItem)
                }
            },
            mapItem(item: PriceTier) {
                const euros = (cents: number) => formatEuro(cents);
                const isLast = item.id === self.prices[self.prices.length - 1]?.id;
                var displayValues: DisplayValues = {
                    minimum: {value: item.minimum, toDisplay: euros(item.minimum)},
                    maximum: {value: item.maximum, toDisplay: isLast ? "" : euros(item.maximum + 1)},
                    ratio: {value: item.ratio, toDisplay: item.ratio},
                    discount: {value: item.discount, toDisplay: item.discount + "%"}
                }
                return {
                    displayValues: displayValues,
                    id: item.id
                }
            },
            getCheckoutSessionUrl: flow(function* (balanceCents: number) {
                const endpoint = endpoints.get("create_shop_checkout_session");
                endpoint.queryParams = `balanceCents=${balanceCents}&language=${translationService.language}`;
                const sessionUrl = yield getEnv(self).api.get(endpoint);
                window.location.href = sessionUrl;
            }),
            // What a balance of balanceCents costs to buy, in euros (the server charges the same:
            // balance x tier ratio, rounded to the cent).
            calculateCurrency: function (balanceCents: number) {
                return Math.round(balanceCents * tierFor(balanceCents).ratio) / 100;
            },
            getDiscount: function (balanceCents: number) {
                return tierFor(balanceCents).discount;
            }
        }
    })
    .views((self) => ({
        get endpoint() {
            return endpoints.get("get_prices");
        },
        get isLoading() {
            return self.prices.length < 3;
        },
        get coinAxis() {
            if (!this.maximumCoins || this.maximumCoins <= 0) return [];
            // Use conversion price as step normally. When conversion is free
            // (or invalid), fall back to ~500 evenly-spaced points across the
            // range — this matches the visual density a small positive
            // conversion price would produce, so the chart shape stays
            // consistent with the non-free case.
            var steps = getEnv(self).activityPricesStorage.conversionPrice;
            if (!Number.isFinite(steps) || steps <= 0) {
                steps = Math.max(1, Math.floor(this.maximumCoins / 500));
            }
            const count = Math.floor(this.maximumCoins / steps);
            if (!Number.isFinite(count) || count <= 0) return [];
            return Array(count).fill(0).map((_, index) => index * steps);
        },
        get currencyAxis() {
            return this.coinAxis.map((value: number) => {
                return self.calculateCurrency(value);
            });
        },
        get dataset() {
            return this.currencyAxis.map((value: number) => {
                return { x: this.currencyAxis, y: this.coinAxis };
            });
        },
        get minimumCoins() {
            return self.prices[0].minimum;
        },
        get maximumCoins() {
            return self.prices[self.prices.length - 1].maximum;
        },
        get minimumCurrency() {
            return self.calculateCurrency(this.minimumCoins);
        },
        get maximumCurrency() {
            return self.calculateCurrency(this.maximumCoins);
        },
        get totalTiers() {
            return self.prices.length;
        }
    }));

export type PricesStorage = Instance<typeof PricesStorageModel>;
